import React, { useEffect, useMemo, useState } from "react";
import { Building2, ArrowLeft, ChevronRight, Clock, TrendingUp, TrendingDown, Minus, Target, FileDown, Sliders, Calendar, ShieldAlert, UserCog } from "lucide-react";
import { Navigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine } from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import KpiEditor from "@/components/KpiEditor";
import UeSignedReportTab from "@/components/UeSignedReportTab";
import { useAuth } from "@/contexts/AuthContext";
import DataAuditTab from "@/components/DataAuditTab";
import DesignerAccessTab from "@/components/DesignerAccessTab";
import CcSettingsTab from "@/components/CcSettingsTab";
import TraegerProfileEditor from "@/components/TraegerProfileEditor";

const monthIso = () => new Date().toISOString().slice(0, 7);
const fmtMin = (m) => { const h = Math.floor(m / 60); const r = m % 60; return h ? `${h}h ${r ? r + "m" : ""}`.trim() : `${r || 0}m`; };
const MONTH_NAMES_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const fmtMonthDe = (ym) => {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  return `${MONTH_NAMES_DE[parseInt(m, 10) - 1] || m} ${y}`;
};
const AMPEL_DOT = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444", gray: "#94A3B8" };
const AMPEL_CLS = {
  green:  "bg-emerald-100 text-emerald-800 border-emerald-300",
  yellow: "bg-amber-100 text-amber-800 border-amber-300",
  red:    "bg-red-100 text-red-800 border-red-300",
  gray:   "bg-slate-100 text-slate-600 border-slate-300",
};
const scoreColor = (lvl) => lvl === "green" ? "text-emerald-700" : lvl === "yellow" ? "text-amber-700" : lvl === "red" ? "text-red-700" : "text-slate-400";

const prevMonthIso = (m) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function KpiValueEditor({ k, userId, month, onSaved }) {
  const [edit, setEdit] = React.useState(false);
  const [val, setVal] = React.useState("");
  const stop = (e) => e.stopPropagation();
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const save = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (val === "" || Number(val) < 0) return;
    try { await api.post("/kpi/measurements", { kpi_id: k.id, user_id: userId, month, value: Number(val) }); toast.success("KPI-Wert gespeichert"); setEdit(false); onSaved(); } catch (x) { err(x); }
  };
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Eingetragenen Wert für „${k.name}“ löschen?${k.source !== "manual" ? " Danach gilt wieder der automatische Vorschlag." : ""}`)) return;
    try { await api.delete("/kpi/measurements", { params: { kpi_id: k.id, user_id: userId, month } }); toast.success("Wert gelöscht"); onSaved(); } catch (x) { err(x); }
  };
  if (edit) return (
    <form onSubmit={save} onClick={stop} className="flex items-center gap-1">
      <input autoFocus type="number" min="0" step="any" value={val} onChange={(e) => setVal(e.target.value)} className="w-20 border border-slate-300 px-1.5 py-0.5 text-sm font-mono" data-testid={`mgmt-kpi-input-${k.id}`} />
      <button type="submit" className="text-[11px] px-2 py-0.5 bg-slate-900 text-white" data-testid={`mgmt-kpi-save-${k.id}`}>OK</button>
      <button type="button" onClick={(e) => { e.stopPropagation(); setEdit(false); }} className="text-[11px] px-1 text-slate-500">✕</button>
    </form>
  );
  return (
    <span className="flex items-center gap-2" onClick={stop}>
      <span className="font-mono text-sm" title={k.overridden ? `vom Management eingetragen${k.auto_value != null ? ` · Automatik: ${k.auto_value}` : ""}` : k.source !== "manual" ? "automatischer Vorschlag" : ""}>
        {k.value == null ? "—" : k.value}<span className="text-slate-400 text-[10px]"> / {k.target}</span>
      </span>
      {k.overridden ? <span className="text-[9px] uppercase tracking-overline text-[#0a788c]">manuell</span> : k.source !== "manual" && k.value != null ? <span className="text-[9px] uppercase tracking-overline text-slate-400">Vorschlag</span> : null}
      <button onClick={() => { setVal(k.value ?? ""); setEdit(true); }} className="text-[11px] underline text-slate-500 hover:text-slate-900" data-testid={`mgmt-kpi-edit-${k.id}`}>{k.overridden || k.source === "manual" ? "ändern" : "überschreiben"}</button>
      {k.overridden && <button onClick={remove} className="text-[11px] underline text-red-600" data-testid={`mgmt-kpi-delete-${k.id}`}>löschen</button>}
    </span>
  );
}

export default function ManagementPage() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "drilldown"); // drilldown | team-ampel
  const [month, setMonth] = useState(monthIso());
  const [level, setLevel] = useState("company"); // company | employee | kpi
  const [teamScorecard, setTeamScorecard] = useState(null);
  const [teamOverview, setTeamOverview] = useState(null);
  const [teamTime, setTeamTime] = useState(null);
  const [teamAmpelTime, setTeamAmpelTime] = useState(null);
  const [teamAmpelTimeRange, setTeamAmpelTimeRange] = useState("month");
  const [companyGoals, setCompanyGoals] = useState(null);
  const [companyGoalsSaving, setCompanyGoalsSaving] = useState(false);
  const [emp, setEmp] = useState(null);
  const [empData, setEmpData] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [history, setHistory] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [calendarLinks, setCalendarLinks] = useState(null);
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get("/kpi/management-report.pdf", { params: { month }, responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Management-Bericht_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF-Bericht heruntergeladen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setPdfLoading(false); }
  };

  useEffect(() => {
    Promise.all([
      api.get("/kpi/team-scorecard", { params: { month } }),
      api.get("/kpi/team-overview", { params: { month } }),
      api.get("/kpi/team-time-summary", { params: { range: "month", date: `${month}-15` } }),
    ]).then(([a, b, c]) => { setTeamScorecard(a.data); setTeamOverview(b.data); setTeamTime(c.data); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [month]);

  useEffect(() => {
    if (tab !== "team-ampel") return;
    api.get("/kpi/team-time-summary", { params: { range: teamAmpelTimeRange } })
      .then((r) => setTeamAmpelTime(r.data)).catch(() => {});
  }, [tab, teamAmpelTimeRange]);

  useEffect(() => {
    if (tab !== "team-ampel") return;
    api.get("/kpi/company-goals").then((r) => setCompanyGoals(r.data)).catch(() => {});
    api.get("/calendar/team-feed-links").then((r) => setCalendarLinks(r.data.staff)).catch(() => {});
  }, [tab]);

  const copyLink = async (userId, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLinkId(userId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch { /* ignore */ }
  };

  const excludeFromTeamCalendar = async (email, name) => {
    try {
      await api.post(`/calendar/team-appointments-calendar/exclude?email=${encodeURIComponent(email)}`);
      toast.success(`${name} wurde aus dem geteilten "Alle Termine"-Kalender entfernt`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const saveCompanyGoals = async () => {
    setCompanyGoalsSaving(true);
    try {
      const r = await api.put("/kpi/company-goals", companyGoals);
      setCompanyGoals(r.data);
      toast.success("Monatsziele gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCompanyGoalsSaving(false); }
  };

  const reloadEmp = () => emp && openEmployee(emp);
  const openEmployee = async (row) => {
    setEmp(row); setLevel("employee"); setEmpData(null); setKpi(null);
    try {
      const [d, s, t, p, acq] = await Promise.all([
        api.get("/kpi/dashboard", { params: { month, user_id: row.user_id } }),
        api.get("/kpi/scorecard", { params: { month, user_id: row.user_id } }),
        api.get("/kpi/time-summary", { params: { user_id: row.user_id, date: `${month}-15` } }),
        api.get("/kpi/dashboard", { params: { month: prevMonthIso(month), user_id: row.user_id } }).catch(() => ({ data: null })),
        api.get("/kpi/acquired-clients", { params: { month, user_id: row.user_id } }).catch(() => ({ data: null })),
      ]);
      const prevValues = {};
      for (const k of (p.data?.kpis || [])) prevValues[k.id] = k.value;
      setEmpData({ userId: row.user_id, dashboard: d.data, scorecard: s.data, time: t.data, prevValues, prevMonth: prevMonthIso(month), acquired: acq.data });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openKpi = async (k) => {
    setKpi(k); setLevel("kpi"); setHistory(null);
    try {
      const r = await api.get("/kpi/history", { params: { kpi_id: k.id, months: 12, user_id: emp.user_id } });
      setHistory(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const companyRows = useMemo(() => {
    const byId = {};
    for (const r of (teamScorecard?.rows || [])) byId[r.user_id] = { ...r };
    for (const r of (teamOverview?.rows || [])) byId[r.user_id] = { ...(byId[r.user_id] || { user_id: r.user_id, user_name: r.user_name }), ampel: r.totals, role_name: r.role_name, role_color: r.role_color };
    for (const r of (teamTime?.rows || [])) {
      if (byId[r.user_id]) byId[r.user_id].total_minutes = r.total_minutes;
      else byId[r.user_id] = { user_id: r.user_id, user_name: r.user_name, role_name: r.role_name, role_color: r.role_color, total_minutes: r.total_minutes };
    }
    return Object.values(byId);
  }, [teamScorecard, teamOverview, teamTime]);

  const companyStats = useMemo(() => {
    const scores = companyRows.filter((r) => r.combined_score != null && r.level && r.level !== "gray").map((r) => r.combined_score);
    const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
    const ampel = { green: 0, yellow: 0, red: 0, gray: 0 };
    for (const r of companyRows) for (const k of Object.keys(ampel)) ampel[k] += r.ampel?.[k] || 0;
    const totalMin = companyRows.reduce((s, r) => s + (r.total_minutes || 0), 0);
    return { avg, ampel, totalMin, count: companyRows.length };
  }, [companyRows]);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1300px] mx-auto" data-testid="management-page">
      {/* Header + Breadcrumb */}
      <div className="flex items-end justify-between mb-6 border-b border-slate-200 pb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Geschäftsführung</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">Management-Drilldown</h1>
          <div className="flex items-center gap-1.5 mt-3 text-xs" data-testid="mgmt-breadcrumb">
            <button onClick={() => { setLevel("company"); setEmp(null); setKpi(null); }}
              className={`flex items-center gap-1 px-2 py-1 ${level === "company" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="mgmt-crumb-company">
              <Building2 size={11} strokeWidth={1.5} /> Firma
            </button>
            {emp && (<>
              <ChevronRight size={12} className="text-slate-400" />
              <button onClick={() => { setLevel("employee"); setKpi(null); }}
                className={`px-2 py-1 ${level === "employee" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid="mgmt-crumb-employee">{emp.user_name}</button>
            </>)}
            {kpi && (<>
              <ChevronRight size={12} className="text-slate-400" />
              <span className="px-2 py-1 bg-slate-900 text-white" data-testid="mgmt-crumb-kpi">{kpi.name}</span>
            </>)}
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Monat</label>
            <input type="month" className="input-tech font-mono" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="mgmt-month" />
          </div>
          <button onClick={downloadPdf} disabled={pdfLoading}
            className="px-4 py-2.5 bg-slate-900 text-white text-xs font-medium hover:bg-black flex items-center gap-1.5 disabled:opacity-50"
            data-testid="mgmt-pdf-btn">
            <FileDown size={12} strokeWidth={1.5} /> {pdfLoading ? "Erstellt…" : "PDF-Bericht"}
          </button>
        </div>
      </div>

      {/* Tabs: Drilldown (messen) vs Team-Ampel (Ziele festlegen) */}
      <div className="flex border-b border-slate-200 mb-6 gap-1 overflow-x-auto" data-testid="mgmt-tabs">
        {[
          { k: "drilldown",  l: "Drilldown-Report", icon: Building2 },
          { k: "team-ampel", l: "Team-Ampel",        icon: Sliders },
          { k: "ue-bericht", l: "UE-Bericht (Coach/Dozent)", icon: Clock },
          { k: "data-audit", l: "Datenprüfung",          icon: ShieldAlert },
          { k: "traegerdaten", l: "Trägerdaten, Stempel & Unterschrift", icon: UserCog },
          { k: "cc", l: "E-Mail-Kopien (CC)", icon: UserCog },
          { k: "branding",   l: "Branding",              icon: UserCog },
        ].map(({ k, l, icon: Ico }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`mgmt-tab-${k}`}
            className={`px-4 py-2.5 text-xs font-medium flex items-center gap-2 border-b-2 ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <Ico size={12} strokeWidth={1.5} /> {l}
          </button>
        ))}
      </div>

      {tab === "drilldown" && (<>
      {/* EBENE 1: Firma */}
      {level === "company" && (
        <div data-testid="mgmt-company">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <div className="border border-slate-200 bg-white p-4" data-testid="mgmt-stat-score">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Ø Team-Score</div>
              <div className="font-heading text-3xl font-light mt-1 text-slate-900">{companyStats.avg ?? "—"}<span className="text-sm text-slate-400">/100</span></div>
            </div>
            <div className="border border-slate-200 bg-white p-4" data-testid="mgmt-stat-ampel">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">KPI-Ampel gesamt</div>
              <div className="flex items-center gap-3 mt-2 font-mono text-sm">
                <span className="text-emerald-700">● {companyStats.ampel.green}</span>
                <span className="text-amber-700">● {companyStats.ampel.yellow}</span>
                <span className="text-red-700">● {companyStats.ampel.red}</span>
              </div>
            </div>
            <div className="border border-slate-200 bg-white p-4" data-testid="mgmt-stat-time">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Erfasste Zeit · Monat</div>
              <div className="font-heading text-3xl font-light mt-1 text-slate-900">{fmtMin(companyStats.totalMin)}</div>
            </div>
            <div className="border border-slate-200 bg-white p-4" data-testid="mgmt-stat-count">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Mitarbeiter mit Rolle</div>
              <div className="font-heading text-3xl font-light mt-1 text-slate-900">{companyStats.count}</div>
            </div>
          </div>
          <div className="border border-slate-200 bg-white" data-testid="mgmt-employee-table">
            <div className="p-5 border-b border-slate-200">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Ebene 1 · Firma → Mitarbeiter</div>
              <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Klicke auf einen Mitarbeiter für Details</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-3 font-medium">Mitarbeiter</th>
                    <th className="text-left p-3 font-medium">Rolle</th>
                    <th className="text-right p-3 font-medium">Menge (KPIs)</th>
                    <th className="text-right p-3 font-medium">Zeit</th>
                    <th className="text-right p-3 font-medium">Score</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {companyRows.map((r) => (
                    <tr key={r.user_id} onClick={() => openEmployee(r)}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      data-testid={`mgmt-emp-row-${r.user_id}`}>
                      <td className="p-3 font-medium text-slate-900">{r.user_name}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1 text-[10px]">
                          <span className="w-2 h-2" style={{ background: r.role_color || r.role?.color || "#94A3B8" }} />
                          {r.role_name || r.role?.name || "—"}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono">{r.quantity?.score == null ? "—" : `${r.quantity.score}%`}</td>
                      <td className="p-3 text-right font-mono">{fmtMin(r.total_minutes || 0)}</td>
                      <td className={`p-3 text-right font-mono font-bold ${scoreColor(r.level)}`}>{r.combined_score ?? "—"}</td>
                      <td className="p-3 text-slate-400"><ChevronRight size={13} /></td>
                    </tr>
                  ))}
                  {companyRows.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-400 italic">Keine Mitarbeiter mit WAAT-Rolle.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* EBENE 2: Mitarbeiter */}
      {level === "employee" && emp && (
        <div data-testid="mgmt-employee">
          <button onClick={() => { setLevel("company"); setEmp(null); }}
            className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 mb-4" data-testid="mgmt-back-company">
            <ArrowLeft size={12} /> Zurück zur Firma
          </button>
          {!empData ? <div className="text-sm text-slate-400 italic py-10 text-center">Lädt…</div> : (<>
            {empData.scorecard?.assigned && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="border border-slate-200 bg-white p-4">
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Score</div>
                  <div className={`font-heading text-3xl font-light mt-1 ${scoreColor(empData.scorecard.level)}`}>{empData.scorecard.combined_score ?? 0}<span className="text-sm text-slate-400">/100</span></div>
                </div>
                <div className="border border-slate-200 bg-white p-4">
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Menge (KPI-Zielerreichung)</div>
                  <div className="font-mono text-2xl mt-1">{empData.scorecard.quantity?.score == null ? "—" : `${empData.scorecard.quantity.score}%`}</div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-slate-200 bg-white" data-testid="mgmt-emp-kpis">
                <div className="p-5 border-b border-slate-200">
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Target size={11} /> Ebene 2 → Klick auf KPI für Verlauf</div>
                  <div className="font-heading text-xl font-light tracking-tighter text-slate-900">KPIs · {fmtMonthDe(month)}</div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {(empData.dashboard?.kpis || []).map((k) => {
                    const prev = empData.prevValues?.[k.id];
                    const trend = k.value == null || prev == null ? null : k.value > prev ? "up" : k.value < prev ? "down" : "flat";
                    return (
                    <li key={k.id} onClick={() => openKpi(k)}
                      className="p-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer"
                      data-testid={`mgmt-kpi-${k.id}`}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: AMPEL_DOT[k.ampel] || AMPEL_DOT.gray }} />
                      <div className="flex-1">
                        <div className="text-xs font-medium text-slate-900">{k.name}</div>
                        <div className="text-[10px] text-slate-500">Ziel {k.target} {k.unit} · {k.period === "weekly" ? "wöchentlich" : "monatlich"}</div>
                      </div>
                      {trend && (
                        <span title={`Vormonat (${empData.prevMonth}): ${prev}`} data-testid={`mgmt-kpi-trend-${k.id}`}
                          className={`inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 border ${
                            trend === "up" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : trend === "down" ? "border-red-200 bg-red-50 text-red-700"
                            : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                          {trend === "up" ? <TrendingUp size={10} strokeWidth={2} /> : trend === "down" ? <TrendingDown size={10} strokeWidth={2} /> : <Minus size={10} strokeWidth={2} />}
                          {trend === "flat" ? "±0" : `${k.value > prev ? "+" : ""}${Math.round((k.value - prev) * 10) / 10}`}
                        </span>
                      )}
                      <KpiValueEditor k={k} userId={empData.userId} month={month} onSaved={() => reloadEmp()} />
                      <ChevronRight size={13} className="text-slate-400" />
                    </li>
                    );
                  })}
                  {(empData.dashboard?.kpis || []).length === 0 && <li className="p-6 text-center text-slate-400 italic text-sm">Keine KPIs (keine WAAT-Rolle).</li>}
                </ul>
              </div>
              <div className="border border-slate-200 bg-white p-5" data-testid="mgmt-emp-time">
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Clock size={11} /> Zeit-Verteilung · Monat</div>
                <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-3">Wofür nutzt {emp.user_name} die Zeit?</div>
                {(() => {
                  const agg = empData.time?.month;
                  const data = (agg?.by_category || []).map((c) => ({ name: c.name, value: c.minutes, color: c.color }));
                  if (!data.length) return <div className="text-sm text-slate-400 italic text-center py-12">Keine Zeit erfasst.</div>;
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3 items-center">
                      <div className="relative">
                        <ResponsiveContainer width="100%" height={190}>
                          <PieChart>
                            <Pie data={data} innerRadius={52} outerRadius={82} paddingAngle={2} dataKey="value" stroke="none">
                              {data.map((e, i) => <Cell key={i} fill={e.color} />)}
                            </Pie>
                            <Tooltip formatter={(v) => fmtMin(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="font-heading text-lg font-light">{fmtMin(agg.total_minutes)}</div>
                        </div>
                      </div>
                      <ul className="space-y-1 text-xs max-h-[190px] overflow-y-auto">
                        {data.map((d) => (
                          <li key={d.name} className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                            <span className="flex-1 truncate">{d.name}</span>
                            <span className="font-mono text-slate-600">{fmtMin(d.value)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </div>
            </div>
            {empData.acquired && (
              <div className="border border-slate-200 bg-white p-5 mt-6" data-testid="mgmt-emp-acquired">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Von {emp.user_name} gewonnene Kunden</div>
                    <div className="font-heading text-xl font-light tracking-tighter text-slate-900">
                      {empData.acquired.total_count} Kunde{empData.acquired.total_count === 1 ? "" : "n"} insgesamt
                      <span className="text-sm text-slate-500 font-sans"> · {empData.acquired.month_count} in {fmtMonthDe(month)}</span>
                    </div>
                  </div>
                </div>
                {empData.acquired.clients?.length > 0 && (
                  <ul className="mt-4 divide-y divide-slate-100 max-h-[220px] overflow-y-auto" data-testid="mgmt-emp-acquired-list">
                    {empData.acquired.clients.map((c) => (
                      <li key={c.client_id} className="py-2 flex items-center justify-between text-xs">
                        <span className="text-slate-800">{c.name}</span>
                        <span className="text-slate-500 font-mono">{c.total_ue} UE · {(c.created_at || "").slice(0, 10).split("-").reverse().join(".")}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>)}
        </div>
      )}

      {/* EBENE 3: KPI-Verlauf */}
      {level === "kpi" && kpi && emp && (
        <div data-testid="mgmt-kpi-detail">
          <button onClick={() => { setLevel("employee"); setKpi(null); }}
            className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 mb-4" data-testid="mgmt-back-employee">
            <ArrowLeft size={12} /> Zurück zu {emp.user_name}
          </button>
          <div className="border border-slate-200 bg-white p-5 mb-6">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><TrendingUp size={11} /> Ebene 3 · 12-Monats-Verlauf</div>
            <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">{kpi.name}</div>
            <div className="text-xs text-slate-500 mt-1">Ziel: {kpi.target} {kpi.unit} · {emp.user_name}</div>
            {!history ? <div className="text-sm text-slate-400 italic py-10 text-center">Lädt…</div> : (() => {
              const tgt = history.target ?? kpi.target ?? 0;
              const maxVal = Math.max(tgt, ...(history.points || []).map((p) => p.value ?? 0));
              const yMax = Math.ceil(maxVal * 1.15) || 10;
              return (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={(history.points || []).map((p) => ({ ...p, value: p.value ?? 0 }))} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, yMax]} />
                  <Tooltip />
                  <ReferenceLine y={tgt} stroke="#EF4444" strokeDasharray="4 4" label={{ value: `Ziel ${tgt}`, fontSize: 10, fill: "#EF4444", position: "insideTopRight" }} />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    {(history.points || []).map((p, i) => {
                      const v = p.value ?? 0; const t = tgt;
                      const c = t <= 0 || p.value == null ? "#CBD5E1" : v >= 0.9 * t ? "#10B981" : v >= 0.6 * t ? "#F59E0B" : "#EF4444";
                      return <Cell key={i} fill={c} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              );
            })()}
          </div>
          {history && (
            <div className="border border-slate-200 bg-white" data-testid="mgmt-kpi-table">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-3 font-medium">Monat</th>
                    <th className="text-right p-3 font-medium">Ist</th>
                    <th className="text-right p-3 font-medium">Ziel</th>
                    <th className="text-right p-3 font-medium">Erreichung</th>
                  </tr>
                </thead>
                <tbody>
                  {(history.points || []).slice().reverse().map((p) => {
                    const t = history.target ?? kpi.target ?? 0;
                    const pct = p.value != null && t > 0 ? Math.round((100 * p.value) / t) : null;
                    return (
                      <tr key={p.month} className="border-b border-slate-100">
                        <td className="p-3 font-mono">{fmtMonthDe(p.month)}</td>
                        <td className="p-3 text-right font-mono">{p.value ?? "—"}</td>
                        <td className="p-3 text-right font-mono text-slate-500">{t}</td>
                        <td className={`p-3 text-right font-mono ${pct == null ? "text-slate-400" : pct >= 90 ? "text-emerald-700" : pct >= 60 ? "text-amber-700" : "text-red-700"}`}>{pct == null ? "—" : `${pct}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </>)}

      {/* TAB: Team-Ampel — Ziele festlegen (KPI-Editor) + messen (Scorecard/Zeit/Ampel-Board) */}
      {tab === "team-ampel" && (
        <div data-testid="mgmt-team-ampel">
          <div className="border border-slate-200 bg-white p-5 mb-8" data-testid="team-calendar-links-card">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
              <Calendar size={11} strokeWidth={1.5} /> Team-Kalender-Links
            </div>
            <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-1">
              Jeder Coach & Dozent hat einen eigenen Kalender-Link
            </div>
            <p className="text-xs text-slate-500 mb-4 max-w-2xl">
              Link kopieren und im eigenen Google/Apple-Kalender per „Kalender abonnieren per URL" einbetten —
              jeder Coach/Dozent bekommt hier eine eigene Farbe zur Wiedererkennung.
            </p>
            {!calendarLinks ? (
              <div className="text-sm text-slate-400 italic py-2">Lädt…</div>
            ) : calendarLinks.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-2">Noch keine Coaches/Dozenten angelegt.</div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="team-calendar-links-list">
                {calendarLinks.map((s) => (
                  <li key={s.user_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`calendar-link-row-${s.user_id}`}>
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="text-sm font-medium text-slate-900 w-40 flex-shrink-0 truncate">{s.name}</span>
                    <span className="text-[10px] uppercase tracking-overline text-slate-400 w-20 flex-shrink-0">{s.role === "lecturer" ? "Dozent" : "Coach"}</span>
                    <span className="flex-1 min-w-0 font-mono text-[11px] text-slate-600 truncate bg-slate-50 border border-slate-200 px-2 py-1">{s.feed_url}</span>
                    <button onClick={() => copyLink(s.user_id, s.feed_url)} data-testid={`calendar-link-copy-${s.user_id}`}
                      className="text-[10px] font-medium text-[#00807E] hover:underline flex-shrink-0">
                      {copiedLinkId === s.user_id ? "✓ Kopiert" : "Kopieren"}
                    </button>
                    <a href={s.webcal_url} data-testid={`calendar-link-open-${s.user_id}`}
                      className="text-[10px] font-medium text-slate-500 hover:underline flex-shrink-0">
                      In Kalender öffnen
                    </a>
                    {s.email && (
                      <button onClick={() => excludeFromTeamCalendar(s.email, s.name)} data-testid={`calendar-link-exclude-${s.user_id}`}
                        title="Diese Person NICHT mehr im geteilten 'Alle Termine'-Team-Kalender anzeigen (z. B. wenn sie selbst nur ihre eigenen Termine sehen soll)"
                        className="text-[10px] font-medium text-slate-400 hover:text-red-600 hover:underline flex-shrink-0">
                        Aus Team-Übersicht ausschließen
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border border-slate-200 bg-white p-5 mb-8" data-testid="company-goals-editor">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Target size={11} strokeWidth={1.5} /> Team-Erfolg-Widget</div>
            <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-4">Monatsziele (Firma) — erscheinen auf „Team"</div>
            {!companyGoals ? <div className="text-sm text-slate-400 italic py-2">Lädt…</div> : (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Coaching-UEs / Monat</label>
                  <input type="number" min="1" className="input-tech font-mono w-40" data-testid="company-goal-ue-input"
                    value={companyGoals.coaching_ue_target}
                    onChange={(e) => setCompanyGoals({ ...companyGoals, coaching_ue_target: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Neue WB-Teilnehmer / Monat</label>
                  <input type="number" min="1" className="input-tech font-mono w-40" data-testid="company-goal-wb-input"
                    value={companyGoals.wb_participants_target}
                    onChange={(e) => setCompanyGoals({ ...companyGoals, wb_participants_target: e.target.value })} />
                </div>
                <button onClick={saveCompanyGoals} disabled={companyGoalsSaving} data-testid="company-goal-save-btn"
                  className="px-4 py-2.5 bg-slate-900 text-white text-xs font-medium hover:bg-black disabled:opacity-50">
                  {companyGoalsSaving ? "Speichert…" : "Speichern"}
                </button>
              </div>
            )}
          </div>

          <KpiEditor />

          {teamScorecard && teamScorecard.rows && teamScorecard.rows.length > 0 && (
            <div className="border border-slate-200 bg-white mb-8 mt-8" data-testid="team-scorecard">
              <div className="p-5 border-b border-slate-200">
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><TrendingUp size={11} strokeWidth={1.5} /> Team-Scorecard · {fmtMonthDe(teamScorecard.month)}</div>
                <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Menge (KPI-Zielerreichung) · Score</div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-3 font-medium">Mitarbeiter</th>
                      <th className="text-left p-3 font-medium">Rolle</th>
                      <th className="text-right p-3 font-medium">Menge</th>
                      <th className="text-right p-3 font-medium">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamScorecard.rows.map((r) => (
                      <tr key={r.user_id} className="border-b border-slate-100" data-testid={`team-sc-row-${r.user_id}`}>
                        <td className="p-3 font-medium text-slate-900">{r.user_name}</td>
                        <td className="p-3 text-[10px] text-slate-600">{r.role?.name}</td>
                        <td className="p-3 text-right font-mono">{r.quantity?.score == null ? "—" : `${r.quantity.score}%`}</td>
                        <td className="p-3 text-right">
                          <span className={`inline-flex items-center gap-1 font-mono text-sm font-bold ${scoreColor(r.level)}`}>
                            <span className="w-2 h-2 rounded-full" style={{ background: AMPEL_DOT[r.level] || AMPEL_DOT.gray }} />
                            {r.combined_score}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="border border-slate-200 bg-white mb-8" data-testid="team-time-overview">
            <div className="p-5 border-b border-slate-200 flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Clock size={11} strokeWidth={1.5} /> Team-Zeit-Übersicht</div>
                <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Wer nutzt wie viel Zeit wofür?</div>
              </div>
              <div className="flex border border-slate-300" data-testid="team-time-range-toggle">
                {[["day", "Tag"], ["week", "Woche"], ["month", "Monat"]].map(([k, l]) => (
                  <button key={k} onClick={() => setTeamAmpelTimeRange(k)} data-testid={`team-time-range-${k}`}
                    className={`px-3 py-1.5 text-xs font-medium ${k !== "day" ? "border-l border-slate-300" : ""} ${teamAmpelTimeRange === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {(teamAmpelTime?.rows || []).map((r) => {
                const data = (r.by_category || []).map((c) => ({ name: c.name, value: c.minutes, color: c.color }));
                return (
                  <div key={r.user_id} className="border border-slate-200 p-4" data-testid={`team-time-card-${r.user_id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{r.user_name}</div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-1">
                          <span className="w-2 h-2 inline-block" style={{ background: r.role_color }} />
                          {r.role_name || "—"}
                        </div>
                      </div>
                      <div className="font-mono text-sm text-slate-900">{fmtMin(r.total_minutes)}</div>
                    </div>
                    {data.length === 0 ? (
                      <div className="text-[11px] text-slate-400 italic text-center py-8">Keine Zeit erfasst.</div>
                    ) : (
                      <div className="grid grid-cols-[110px_1fr] gap-2 items-center mt-2">
                        <ResponsiveContainer width="100%" height={110}>
                          <PieChart>
                            <Pie data={data} innerRadius={30} outerRadius={50} paddingAngle={2} dataKey="value" stroke="none">
                              {data.map((e, i) => <Cell key={i} fill={e.color} />)}
                            </Pie>
                            <Tooltip formatter={(v) => fmtMin(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                        <ul className="space-y-0.5 text-[10px] max-h-[110px] overflow-y-auto">
                          {data.slice(0, 5).map((d) => (
                            <li key={d.name} className="flex items-center gap-1.5">
                              <span className="w-2 h-2 shrink-0 rounded-sm" style={{ background: d.color }} />
                              <span className="flex-1 truncate text-slate-800">{d.name}</span>
                              <span className="font-mono text-slate-500">{fmtMin(d.value)}</span>
                            </li>
                          ))}
                          {data.length > 5 && <li className="text-slate-400 italic">+ {data.length - 5} weitere</li>}
                        </ul>
                      </div>
                    )}
                    {r.auto_coaching?.minutes > 0 && (
                      <div className="text-[9px] text-slate-500 mt-1.5">inkl. {r.auto_coaching.count} Coaching-Termine ({fmtMin(r.auto_coaching.minutes)}) automatisch</div>
                    )}
                  </div>
                );
              })}
              {(teamAmpelTime?.rows || []).length === 0 && (
                <div className="col-span-full text-sm text-slate-400 italic text-center py-8">Keine Team-Daten.</div>
              )}
            </div>
          </div>

          {teamOverview && teamOverview.rows && teamOverview.rows.length > 0 && (
            <div className="border border-slate-200 bg-white" data-testid="team-overview">
              <div className="p-5 border-b border-slate-200 flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><TrendingUp size={11} strokeWidth={1.5} /> Team · {fmtMonthDe(teamOverview.month)}</div>
                  <div className="font-heading text-xl font-light tracking-tighter text-slate-900">KPI-Ampel-Board</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-3 font-medium">Mitarbeiter</th>
                      <th className="text-left p-3 font-medium">Rolle</th>
                      <th className="text-center p-3 font-medium">🟢</th>
                      <th className="text-center p-3 font-medium">🟡</th>
                      <th className="text-center p-3 font-medium">🔴</th>
                      <th className="text-center p-3 font-medium">—</th>
                      <th className="text-left p-3 font-medium">KPI-Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamOverview.rows.map((r) => (
                      <tr key={r.user_id} className="border-b border-slate-100" data-testid={`team-row-${r.user_id}`}>
                        <td className="p-3 font-medium text-slate-900">{r.user_name}</td>
                        <td className="p-3">
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium">
                            <span className="w-2 h-2" style={{ background: r.role_color }} />
                            {r.role_name}
                          </span>
                        </td>
                        <td className="p-3 text-center font-mono text-emerald-700">{r.totals.green}</td>
                        <td className="p-3 text-center font-mono text-amber-700">{r.totals.yellow}</td>
                        <td className="p-3 text-center font-mono text-red-700">{r.totals.red}</td>
                        <td className="p-3 text-center font-mono text-slate-500">{r.totals.gray}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {r.kpis.map((k) => (
                              <span key={k.id} className={`text-[10px] px-1.5 py-0.5 border ${AMPEL_CLS[k.ampel] || AMPEL_CLS.gray}`} title={`${k.name}: ${k.value == null ? '—' : k.value}/${k.target} ${k.unit}`}>
                                {k.value == null ? "—" : `${k.value}/${k.target}`}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "ue-bericht" && <UeSignedReportTab />}
      {tab === "invoices" && <Navigate to="/abrechnung?tab=avgs" replace />}
      {tab === "data-audit" && <DataAuditTab />}
      {tab === "branding" && <DesignerAccessTab />}
      {tab === "zulassungen" && <Navigate to="/traeger?tab=zulassungen" replace />}
      {tab === "cc" && <CcSettingsTab />}
      {tab === "traegerdaten" && <div className="max-w-4xl space-y-3"><a href="/einrichtung" className="text-xs underline underline-offset-4 text-slate-600" data-testid="mgmt-open-setup">Einrichtungs-Assistent öffnen</a><TraegerProfileEditor /></div>}
    </div>
  );
}
